import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t } from '../core/i18n.js';
import { popToast } from '../utils/alerts.js';
import { rtcState } from './rtcState.js';
import {
    stopperFluxAudio, appliquerSuppressionBruit,
    afficherSelfHangup, cacherSelfHangup,
    majBoutonSoundboardHeader, quitterVocal,
    vocalOnSound, vocalOffSound,
    afficherSliderVolume
} from './audio.js';
import { appelerAgent, arreterScreenShare, detecterVoix } from './peers.js';

export const ringtoneSound = new Audio('./sounds/ringtone.mp3');
ringtoneSound.loop = true;

// ==========================================================================
//   HELPERS UI — BOUTON APPEL & BULLE VOCAL
// ==========================================================================

export function terminerAppelMPSiActif() {
    const cibleMP = localStorage.getItem('memoireAppelMP');
    if (!cibleMP) return;
    try { socket.emit('mp-stop-definitif', cibleMP); } catch (_) {}
    fermerAppelMPDefinitivement();
}

export function majBoutonAppelMP() {
    const btn = document.getElementById('btn-call-mp');
    if (!btn) return;
    if (!state.destinataireID) { btn.style.display = 'none'; return; }
    const dejaEnAppel = localStorage.getItem('memoireAppelMP') === state.destinataireID;
    btn.style.display = dejaEnAppel ? 'none' : 'inline-block';
    if (!dejaEnAppel) btn.onclick = () => appelerEnMP(state.destinataireID);
}

// ==========================================================================
//   BANDEAU MP — AVATARS
// ==========================================================================

export function configurerAvatarBandeau(elementId, pseudo, fallbackInitiale) {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.dataset.pseudo = pseudo || '';
    const infos      = state.utilisateurs[pseudo] || {};
    const couleur    = infos.couleur || '#58a6ff';
    const initialeEl = el.querySelector('.mpc-initial');
    if (infos.avatar_url && infos.avatar_url !== 'none' && String(infos.avatar_url) !== 'undefined') {
        el.style.backgroundImage = `url('${infos.avatar_url}')`;
        el.classList.add('has-avatar');
        if (initialeEl) initialeEl.style.display = 'none';
    } else {
        el.style.backgroundImage = '';
        el.classList.remove('has-avatar');
        if (initialeEl) {
            initialeEl.style.display = '';
            initialeEl.textContent = (pseudo || fallbackInitiale || '?').charAt(0).toUpperCase();
        }
    }
    el.style.backgroundColor = couleur;
    el.style.borderColor     = couleur;
}

export function mettreAJourAvatarsBandeau(cible) {
    configurerAvatarBandeau('mpc-avatar-self',   state.monPseudo, state.monPseudo);
    configurerAvatarBandeau('mpc-avatar-remote', cible, cible);
    const remoteEl = document.getElementById('mpc-avatar-remote');
    if (remoteEl && cible && !remoteEl.dataset.volumeWired) {
        remoteEl.addEventListener('contextmenu', (e) => {
            const pseudo = remoteEl.dataset.pseudo;
            if (!pseudo || pseudo === state.monPseudo) return;
            e.preventDefault();
            afficherSliderVolume(pseudo, e.clientX, e.clientY);
        });
        let pressTimer = null;
        remoteEl.addEventListener('touchstart', (e) => {
            const pseudo = remoteEl.dataset.pseudo;
            if (!pseudo || pseudo === state.monPseudo) return;
            const touch = e.touches[0];
            pressTimer = setTimeout(() => afficherSliderVolume(pseudo, touch.clientX, touch.clientY), 500);
        });
        const cancelPress = () => { if (pressTimer) clearTimeout(pressTimer); };
        remoteEl.addEventListener('touchend',    cancelPress);
        remoteEl.addEventListener('touchmove',   cancelPress);
        remoteEl.addEventListener('touchcancel', cancelPress);
        remoteEl.dataset.volumeWired = '1';
    }
}

// ==========================================================================
//   PASTILLE SORTANTE & MODAL ENTRANT
// ==========================================================================

export function afficherPastilleSortante(pseudo) {
    const pill     = document.getElementById('outgoing-call-pill');
    const pseudoEl = document.getElementById('outgoing-pill-pseudo');
    const avatarEl = document.getElementById('ocm-avatar');
    const initEl   = document.getElementById('ocm-initial');
    if (!pill) return;
    if (pseudoEl) pseudoEl.textContent = pseudo;
    const infos = state.utilisateurs[pseudo] || {};
    if (avatarEl) {
        if (infos.avatar_url && infos.avatar_url !== 'none' && String(infos.avatar_url) !== 'undefined') {
            avatarEl.style.backgroundImage = `url('${infos.avatar_url}')`;
            avatarEl.classList.add('has-avatar');
        } else {
            avatarEl.style.backgroundImage = '';
            avatarEl.classList.remove('has-avatar');
            if (initEl) initEl.textContent = pseudo.charAt(0).toUpperCase();
        }
        avatarEl.style.backgroundColor = infos.couleur || '#58a6ff';
    }
    pill.style.display = 'flex';
}

export function cacherPastilleSortante() {
    const pill = document.getElementById('outgoing-call-pill');
    if (pill) pill.style.display = 'none';
    rtcState.appelSortantCible = null;
}

export function afficherModalEntrant(callerId) {
    const modal    = document.getElementById('incoming-call-modal');
    const pseudoEl = document.getElementById('icm-pseudo');
    const avatarEl = document.getElementById('icm-avatar');
    const initEl   = document.getElementById('icm-initial');
    if (!modal) return;
    if (pseudoEl) pseudoEl.textContent = callerId;
    const infos = state.utilisateurs[callerId] || {};
    if (avatarEl) {
        if (infos.avatar_url && infos.avatar_url !== 'none' && String(infos.avatar_url) !== 'undefined') {
            avatarEl.style.backgroundImage = `url('${infos.avatar_url}')`;
            avatarEl.classList.add('has-avatar');
        } else {
            avatarEl.style.backgroundImage = '';
            avatarEl.classList.remove('has-avatar');
            if (initEl) initEl.textContent = callerId.charAt(0).toUpperCase();
        }
        avatarEl.style.backgroundColor = infos.couleur || '#58a6ff';
    }
    modal.style.display = 'flex';
    rtcState.appelEntrantCallerId = callerId;
    ringtoneSound.play().catch(() => {});
}

export function cacherModalEntrant() {
    const modal = document.getElementById('incoming-call-modal');
    if (modal) modal.style.display = 'none';
    rtcState.appelEntrantCallerId = null;
    ringtoneSound.pause();
    ringtoneSound.currentTime = 0;
}

// ==========================================================================
//   COUNTDOWN REJOIN
// ==========================================================================

export function demarrerCountdownRejoin(secondes) {
    arreterCountdownRejoin();
    let restant = secondes;
    const lbl = document.getElementById('mpc-rejoin-timer');
    const ecrire = () => {
        if (lbl) {
            const m = Math.floor(restant / 60);
            const s = String(restant % 60).padStart(2, '0');
            lbl.textContent = `${m}:${s}`;
        }
    };
    ecrire();
    rtcState.countdownRejoinInterval = setInterval(() => {
        restant--;
        if (restant <= 0) arreterCountdownRejoin();
        else ecrire();
    }, 1000);
}

export function arreterCountdownRejoin() {
    if (rtcState.countdownRejoinInterval) {
        clearInterval(rtcState.countdownRejoinInterval);
        rtcState.countdownRejoinInterval = null;
    }
}

// ==========================================================================
//   FLUX D'APPEL MP
// ==========================================================================

// 0. Initier un appel
export function appelerEnMP(pseudoCible) {
    if (pseudoCible && localStorage.getItem('memoireAppelMP') === pseudoCible) {
        popToast(`Tu es déjà en appel avec ${pseudoCible}.`, 'stream');
        if (typeof window.majVisibiliteBandeauAppel === 'function') {
            window.majVisibiliteBandeauAppel('mp', { pseudo: pseudoCible });
        }
        return;
    }
    socket.emit('demander-appel-mp', pseudoCible);
    rtcState.appelSortantCible = pseudoCible;

    const banner = document.getElementById('mp-call-banner');
    if (banner) {
        banner.dataset.cible = pseudoCible;
        const status = document.getElementById('mp-call-status');
        if (status) status.innerText = t('call.outgoing', { pseudo: pseudoCible });
        document.getElementById('mp-active-controls').style.display  = 'none';
        document.getElementById('mp-rejoin-controls').style.display  = 'none';
    }
    afficherPastilleSortante(pseudoCible);
}

// Annuler avant réponse
export function annulerAppelSortant() {
    if (!rtcState.appelSortantCible) return;
    socket.emit('annuler-appel-mp', rtcState.appelSortantCible);
    cacherPastilleSortante();
    const banner = document.getElementById('mp-call-banner');
    if (banner) { banner.style.display = 'none'; banner.dataset.cible = ''; }
}

// 1. Réception d'un appel entrant
socket.on('appel-mp-entrant', (data) => {
    afficherModalEntrant(data.callerId);
    const banner = document.getElementById('mp-call-banner');
    if (banner) banner.dataset.cible = data.callerId;
});

// 2. Répondre
export function repondreAppelMP(accepte) {
    const cible = rtcState.appelEntrantCallerId || (document.getElementById('mp-call-banner') || {}).dataset?.cible;
    if (!cible) return;
    socket.emit('reponse-appel-mp', { callerId: cible, accepte });
    cacherModalEntrant();
    if (accepte) {
        demarrerAppelMP(cible, false);
    } else {
        const banner = document.getElementById('mp-call-banner');
        if (banner) { banner.style.display = 'none'; banner.dataset.cible = ''; }
    }
}

// 3. Résultat côté appelant
socket.on('resultat-appel-mp', (data) => {
    cacherPastilleSortante();
    if (data.accepte) {
        popToast(t('toast.call_picked_up', { pseudo: data.targetId }), 'success');
        demarrerAppelMP(data.targetId, true);
    } else {
        popToast(t('toast.call_refused', { pseudo: data.targetId }), 'error');
        const banner = document.getElementById('mp-call-banner');
        if (banner) { banner.style.display = 'none'; banner.dataset.cible = ''; }
    }
});

// 4. Raccrocher (pause ou fin définitive)
export function raccrocherMP() {
    const banner = document.getElementById('mp-call-banner');
    const cible  = banner ? banner.dataset.cible : null;

    vocalOffSound.play().catch(() => {});

    if (window.partenaireEnPause) {
        socket.emit('mp-stop-definitif', cible);
        fermerAppelMPDefinitivement();
        return;
    }

    stopperFluxAudio();
    arreterScreenShare();
    Object.keys(rtcState.connexionsPairs).forEach(p => { rtcState.connexionsPairs[p].close(); delete rtcState.connexionsPairs[p]; });
    document.querySelectorAll('.stream-video-element, .live-badge').forEach(el => el.remove());
    localStorage.removeItem('memoireAppelMP');
    majBoutonSoundboardHeader();
    majBoutonAppelMP();

    const sbPanel = document.getElementById('soundboard-panel');
    if (sbPanel) sbPanel.style.display = 'none';

    const aura = document.getElementById('vocal-aura');
    if (aura) aura.classList.remove('is-vocal-active');

    const btnDisconnect = document.getElementById('vocal-disconnect-btn');
    if (btnDisconnect) btnDisconnect.style.display = 'none';
    const btnStream = document.getElementById('btn-stream');
    if (btnStream) btnStream.style.display = 'none';
    const roomNameDisplay = document.getElementById('vocal-active-room-name');
    if (roomNameDisplay) roomNameDisplay.innerText = '...';

    cacherSelfHangup();

    document.getElementById('mp-active-controls').style.display  = 'none';
    document.getElementById('mp-rejoin-controls').style.display  = 'flex';
    document.getElementById('mp-call-status').innerHTML = t('call.suspended');
    const btnPipMpOff = document.getElementById('btn-pip-mp');
    if (btnPipMpOff) btnPipMpOff.style.display = 'none';
    demarrerCountdownRejoin(180);

    socket.emit('mp-pause', cible);

    clearTimeout(window.timerMP);
    window.timerMP = setTimeout(() => {
        socket.emit('mp-stop-definitif', cible);
        fermerAppelMPDefinitivement();
    }, 180000);
}

// 5. Revenir avant fin du délai
export function revenirAppelMP() {
    clearTimeout(window.timerMP);
    arreterCountdownRejoin();
    const cible = document.getElementById('mp-call-banner').dataset.cible;
    socket.emit('mp-reprise', cible);
    demarrerAppelMP(cible, true);
}

// 6. Partenaire en pause / revient
socket.on('mp-partenaire-en-pause', () => {
    window.partenaireEnPause = true;
    const rejoinControls = document.getElementById('mp-rejoin-controls');
    if (rejoinControls && rejoinControls.style.display === 'flex') {
        fermerAppelMPDefinitivement();
        return;
    }
    document.getElementById('mp-call-status').innerHTML = t('call.partner_absent');
    demarrerCountdownRejoin(180);
    clearTimeout(window.timerMP);
    window.timerMP = setTimeout(() => {
        popToast(t('toast.call_ended'), 'error');
        fermerAppelMPDefinitivement();
    }, 180000);
});

socket.on('mp-partenaire-revient', () => {
    window.partenaireEnPause = false;
    clearTimeout(window.timerMP);
    arreterCountdownRejoin();
    const cible = document.getElementById('mp-call-banner').dataset.cible;
    document.getElementById('mp-call-status').innerText = t('call.active', { pseudo: cible });
});

socket.on('mp-fermeture-totale', () => {
    popToast(t('toast.call_closed'), 'error');
    fermerAppelMPDefinitivement();
});

// 7. Démarrer l'appel (micro + UI)
export async function demarrerAppelMP(cible, estAppelant) {
    if (localStorage.getItem('memoireVocalId')) {
        try { quitterVocal(); } catch (_) {}
    }
    window.partenaireEnPause = false;
    arreterCountdownRejoin();
    cacherPastilleSortante();
    cacherModalEntrant();
    try {
        if (!rtcState.fluxAudioLocal) {
            rtcState.fluxAudioBrut  = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: true },
                video: false
            });
            rtcState.fluxAudioLocal = await appliquerSuppressionBruit(rtcState.fluxAudioBrut);

            detecterVoix(rtcState.fluxAudioBrut, (parle) => {
                const monAvatar = document.getElementById('self-avatar');
                const mpcSelf   = document.getElementById('mpc-avatar-self');
                const actif     = parle && !rtcState.microCoupe;
                if (monAvatar) monAvatar.classList.toggle('is-speaking-border', actif);
                if (mpcSelf)   mpcSelf.classList.toggle('is-speaking-border', actif);
            });
        }

        popToast(t('toast.mic_secured'), 'success');

        const mpBanner = document.getElementById('mp-call-banner');
        if (mpBanner) {
            mpBanner.dataset.cible = cible;
            document.getElementById('mp-call-status').innerText = t('call.active', { pseudo: cible });
            document.getElementById('mp-rejoin-controls').style.display = 'none';
            document.getElementById('mp-active-controls').style.display = 'flex';
            const btnPipMp = document.getElementById('btn-pip-mp');
            if (btnPipMp && window.chaosNative?.ouvrirPiP) btnPipMp.style.display = 'flex';
            mettreAJourAvatarsBandeau(cible);
            if (typeof window.majVisibiliteBandeauAppel === 'function') {
                window.majVisibiliteBandeauAppel(
                    state.destinataireID === cible ? 'mp' : null,
                    state.destinataireID === cible ? { pseudo: cible } : null
                );
            }
        }

        const aura = document.getElementById('vocal-aura');
        if (aura) aura.classList.add('is-vocal-active');
        afficherSelfHangup(raccrocherMP);
        vocalOnSound.play().catch(() => {});

        const infoPanel = document.getElementById('vocal-info-panel');
        if (infoPanel) infoPanel.style.display = 'block';

        const btnStream = document.getElementById('btn-stream');
        if (btnStream) btnStream.style.display = 'flex';

        const roomNameDisplay = document.getElementById('vocal-active-room-name');
        if (roomNameDisplay) roomNameDisplay.innerText = t('vocal.call_with', { pseudo: cible });

        localStorage.setItem('memoireAppelMP', cible);
        majBoutonSoundboardHeader();
        majBoutonAppelMP();

        if (estAppelant) appelerAgent(cible);
    } catch (err) {
        console.error('❌ Erreur micro MP :', err);
        popToast(t('toast.mic_denied'), 'error');
        const mpBanner = document.getElementById('mp-call-banner');
        if (mpBanner) { mpBanner.style.display = 'none'; mpBanner.dataset.cible = ''; }
        cacherSelfHangup();
    }
}

// 8. Kill switch
export function fermerAppelMPDefinitivement() {
    clearTimeout(window.timerMP);
    window.partenaireEnPause = false;
    arreterCountdownRejoin();

    stopperFluxAudio();
    arreterScreenShare();
    Object.keys(rtcState.connexionsPairs).forEach(p => { rtcState.connexionsPairs[p].close(); delete rtcState.connexionsPairs[p]; });
    document.querySelectorAll('.stream-video-element, .live-badge').forEach(el => el.remove());
    const streamZone = document.getElementById('mpc-stream-zone');
    if (streamZone) { streamZone.innerHTML = ''; streamZone.classList.remove('has-stream'); }
    localStorage.removeItem('memoireAppelMP');
    majBoutonSoundboardHeader();
    majBoutonAppelMP();

    const banner = document.getElementById('mp-call-banner');
    if (banner) { banner.style.display = 'none'; banner.dataset.cible = ''; }
    cacherModalEntrant();
    cacherPastilleSortante();
    cacherSelfHangup();

    const aura = document.getElementById('vocal-aura');
    if (aura) aura.classList.remove('is-vocal-active');

    const infoPanel = document.getElementById('vocal-info-panel');
    if (infoPanel) infoPanel.style.display = 'none';

    const btnDisconnect = document.getElementById('vocal-disconnect-btn');
    if (btnDisconnect) btnDisconnect.style.display = 'none';
    const btnStream = document.getElementById('btn-stream');
    if (btnStream) btnStream.style.display = 'none';
    const roomNameDisplay = document.getElementById('vocal-active-room-name');
    if (roomNameDisplay) roomNameDisplay.innerText = '...';
}
